/**
 * ABD (páginas 06): the gallery the admin's editor cannot write.
 *
 * Marina writes the Páginas in the admin's rich text editor, and that editor has
 * no way to say "gallery". What she can do is put one photograph in a paragraph
 * and press Enter, and a gallery comes out as a run of paragraphs that each hold
 * nothing but an image: 27 in a row on `/pages/portfolio`, five on
 * `/pages/nosso-atelie`. Rendered as written they are a stack of full-width
 * slabs, one under the other, for as long as the run is.
 *
 * CSS alone cannot regroup them. `p:has(> img:only-child)` finds each paragraph,
 * but no selector collects a run of siblings into one box. So this element walks
 * its children, finds the runs, and wraps each in one `div.abd-pagina__galeria`
 * for `abd-pagina.css` to lay out as a grid. Horizon already accepts that admin
 * HTML needs fixing up in the browser — `rte-formatter.js` wraps every `<table>`
 * in the rich text the same way — and this is the same move for photographs.
 *
 * **What is deliberately not here.** The `srcset` and `sizes` that ask the
 * Shopify CDN for a photograph at the size of its box are written in Liquid, in
 * `sections/abd-pagina.liquid`, not in this file. A module script runs after the
 * document is parsed, and by then the browser's preload scanner has already
 * fetched the first photograph at its original size; a `srcset` added from here
 * would fetch it a second time. Written in the markup it is read on the first
 * pass, and it holds with the script blocked. And nothing here touches `sizes`
 * either, though a gallery cell is narrower than the measure and the Liquid
 * cannot know which paragraphs will end up in one: rewriting `sizes` from here
 * was tried, and it reaches photographs whose fetch at the wider size is already
 * in flight, so Chromium asks again at the narrower one and both arrive. Read
 * off the live `portfolio` on 12 September 2026: 27 slides fetched once on one
 * run and 52 responses for the same 27 on the next. A gallery photograph
 * therefore fetches the candidate for the reading measure, which is twice the
 * pixels a 280px cell needs on a one-pixel screen and exactly what it needs on a
 * two-pixel one, and is fetched once.
 *
 * **What survives with JavaScript off.** Everything: the paragraphs are still
 * paragraphs, each photograph still has its `srcset`, and the page is the stack
 * it was. The gallery is an upgrade, the same bargain `abd-announcement-dismiss.js`
 * makes, and like that file it is deliberately not a `@theme/component`
 * Component: it owns no refs and no state, and having no imports is what lets
 * `tests/pagina-galeria.spec.mjs` inject it into a blank page and run it over
 * every shape the editor can produce.
 *
 * Nothing here writes alt text and nothing here should. That is páginas 08.
 */

/**
 * The elements that carry meaning with no words in them, bar the photograph
 * itself. A paragraph holding a photograph *and* one of these is not a figure.
 *
 * The same list as `speaks_without_words` in `sections/abd-pagina.liquid`, less
 * `img`, and it has to be: that pass keeps a wordless paragraph for the same
 * reasons this one refuses to call it a photograph.
 */
const SPEAKS_WITHOUT_WORDS = 'iframe, video, audio, svg, table, embed, object, canvas, input, select, textarea, button, form, hr';

/** The class `abd-pagina.css` lays the gallery out by. */
const GALLERY_CLASS = 'abd-pagina__galeria';

/**
 * Whether a node is a paragraph whose only content is one photograph.
 *
 * A paragraph, not a `div` — the editor writes a `div` per line when somebody
 * presses shift-and-Enter, and those are lines of a block rather than blocks.
 * No words, where `\s` already covers the non-breaking space the editor writes
 * as a literal U+00A0. Exactly one `<img>`, at any depth, because `eventos`
 * wraps its photographs in a `<span>`. And nothing else that speaks without
 * words. A photograph under a linked title, which is how
 * `buques-de-noiva-maravilhosos` writes one, has words and is prose.
 *
 * @param {Node} node
 * @returns {node is HTMLParagraphElement}
 */
const isFigure = (node) =>
  node instanceof HTMLParagraphElement &&
  (node.textContent ?? '').replace(/\s/g, '') === '' &&
  node.querySelectorAll('img').length === 1 &&
  node.querySelector(SPEAKS_WITHOUT_WORDS) === null;

/**
 * Whether a node sits between two figures without separating them.
 *
 * Whitespace is what the Liquid leaves between paragraphs, and a comment is what
 * the editor leaves behind (páginas 04 strips them, but this pass need not rely
 * on it). A text node with words in it is something a customer reads, so it
 * separates.
 *
 * @param {Node} node
 */
const isTransparent = (node) =>
  node.nodeType === Node.COMMENT_NODE ||
  (node.nodeType === Node.TEXT_NODE && (node.textContent ?? '').replace(/\s/g, '') === '');

/**
 * The runs of two or more figure paragraphs among `root`'s children, in order.
 *
 * A run of one is not a gallery: `contato` ends on one portrait and `buques`
 * on one bouquet, and a grid with one cell is a stack with extra markup.
 *
 * @param {ParentNode} root
 * @returns {HTMLParagraphElement[][]}
 */
const galleryRuns = (root) => {
  /** @type {HTMLParagraphElement[][]} */
  const runs = [];
  /** @type {HTMLParagraphElement[]} */
  let run = [];

  const close = () => {
    if (run.length >= 2) runs.push(run);
    run = [];
  };

  for (const node of root.childNodes) {
    if (isFigure(node)) run.push(node);
    else if (!isTransparent(node)) close();
  }
  close();

  return runs;
};

/**
 * Wraps every run in `root` in one gallery container.
 *
 * The container is inserted where the run's first paragraph stood and the
 * paragraphs move into it in order, so the document reads exactly as it did
 * and only the grouping is new. Not one attribute on a photograph is changed:
 * moving an `<img>` does not restart its fetch, and writing to it would.
 *
 * Running it twice changes nothing: a gallery is a `div`, not a paragraph, so
 * on the second pass no run is found.
 *
 * @param {ParentNode} root
 */
const gatherGalleries = (root) => {
  for (const run of galleryRuns(root)) {
    const gallery = document.createElement('div');
    gallery.classList.add(GALLERY_CLASS);
    run[0].before(gallery);
    gallery.append(...run);
  }
};

/** The prose wrapper `sections/abd-pagina.liquid` puts around `page.content`. */
class AbdPaginaProsa extends HTMLElement {
  connectedCallback() {
    gatherGalleries(this);
  }
}

if (!customElements.get('abd-pagina-prosa')) {
  customElements.define('abd-pagina-prosa', AbdPaginaProsa);
}
